import { Injectable, NgZone, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { signal, effect } from '@angular/core';
import { fromEvent, merge, timer, Subscription } from 'rxjs';
import { debounceTime, tap, filter } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class InactivityService {
    private router = inject(Router);
    private authService = inject(AuthService);
    private ngZone = inject(NgZone);

    private inactivityTimeout = 5000 * 60 * 1000; // 5 minutos
    private lastActivity = signal(Date.now());
    private isMonitoring = signal(false);
    private timerSubscription: Subscription | null = null; // Store timer subscription
    /**
     * Suscripción a los eventos de actividad del usuario.
     *
     * Se guarda a propósito: `startMonitoring()` se llama en CADA `NavigationEnd`, y antes esto
     * creaba un juego nuevo de 5 listeners sobre `document` (mousemove, click, keydown, touchstart,
     * scroll) sin soltar los anteriores. Como `fromEvent` corre dentro de la zona de Angular, cada
     * listener acumulado disparaba su propio ciclo de detección de cambios: medido en el navegador,
     * un `mousemove` pasaba de ~6 ms con 10 navegaciones a ~22 ms con 50 (lineal, ~0.4 ms por
     * navegación). Eso era la app trabándose al escribir o mover el mouse tras un rato de uso.
     */
    private activitySubscription: Subscription | null = null;
    /** Rutas públicas (`/landing/*`, `/notfound`): ahí no se cuenta inactividad. */
    private isOnPublicRoute = signal(false);
    /**
     * Candado del logout por inactividad.
     *
     * El timer tickea cada segundo y `logout()` es asíncrono (Cognito + navegación), así que sin
     * candado la misma expiración disparaba un `Auth.signOut()` por segundo hasta que el router
     * llegara al login.
     */
    private isLoggingOut = false;

    constructor() {
        // Listen to route changes to manage monitoring
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            // `urlAfterRedirects` y sin query params: interesa la ruta final, no la que se intentó
            // (p.ej. `/dashboard` que un guard reescribe a `/landing/login?showTotpSetup=false`).
            const basePath = (event.urlAfterRedirects || event.url).split('?')[0];
            this.isOnPublicRoute.set(this.isPublicRoute(basePath));
            if (this.isOnPublicRoute()) {
                this.stopMonitoring();
            } else {
                this.startMonitoring();
            }
        });

        // Effect to handle logout when inactivity timeout is reached
        effect(() => {
            if (this.isMonitoring() && !this.isOnPublicRoute()) {
                this.startInactivityTimer();
            } else {
                this.stopInactivityTimer();
            }
        });
    }

    private isPublicRoute(path: string): boolean {
        return path === '/landing' || path.startsWith('/landing/') || path === '/notfound' || path.startsWith('/buzon');
    }

    // Start monitoring user activity
    startMonitoring(): void {
        if (this.isOnPublicRoute()) {
            return;
        }

        if (!this.isMonitoring()) {
            /**
             * Arranque de un ciclo de sesión nuevo: el reloj se cuenta desde ahora.
             *
             * Sin esto, `lastActivity` se quedaba con el timestamp anterior al logout por
             * inactividad (en el login los listeners están apagados, así que nada lo refrescaba).
             * Al volver a entrar, el primer tick del timer veía `Date.now() - lastActivity` ya
             * vencido y sacaba al usuario al login en el mismo instante en que entraba. Solo se
             * arreglaba recargando la página, porque el servicio se reconstruía.
             */
            this.resetActivity();
            this.isLoggingOut = false;
        }

        this.isMonitoring.set(true);
        this.setupActivityListeners();
    }

    // Stop monitoring user activity
    stopMonitoring(): void {
        this.isMonitoring.set(false);
        this.teardownActivityListeners();
    }

    // Reset the last activity timestamp
    private resetActivity(): void {
        this.lastActivity.set(Date.now());
    }

    // Set up listeners for user interactions
    private setupActivityListeners(): void {
        // Idempotente: si ya hay un juego de listeners vivo, no se crea otro.
        if (this.activitySubscription) {
            return;
        }

        const activityEvents = merge(fromEvent(document, 'mousemove'), fromEvent(document, 'click'), fromEvent(document, 'keydown'), fromEvent(document, 'touchstart'), fromEvent(document, 'scroll'));

        // Fuera de la zona: esto solo escribe un timestamp, no pinta nada. Dentro de la zona,
        // cada mousemove forzaba un ciclo de detección de cambios sobre los 225 componentes
        // de la app (de los cuales solo 1 usa OnPush).
        this.ngZone.runOutsideAngular(() => {
            this.activitySubscription = activityEvents
                .pipe(
                    debounceTime(100), // Prevent excessive updates
                    tap(() => this.resetActivity())
                )
                .subscribe();
        });
    }

    // Tear down the activity listeners
    private teardownActivityListeners(): void {
        if (this.activitySubscription) {
            this.activitySubscription.unsubscribe();
            this.activitySubscription = null;
        }
    }

    // Start the inactivity timer
    private startInactivityTimer(): void {
        // Ensure any existing subscription is cleared
        this.stopInactivityTimer();

        // También fuera de la zona: el tick de cada segundo solo compara timestamps. Se vuelve
        // a entrar a la zona únicamente para el logout, que sí navega y tiene que renderizar.
        // El primer tick va al segundo 1, no al 0: nunca se evalúa la expiración en el mismo
        // instante en que arranca el monitoreo.
        this.ngZone.runOutsideAngular(() => {
            this.timerSubscription = timer(1000, 1000).subscribe(() => {
                if (this.isLoggingOut || !this.isMonitoring() || this.isOnPublicRoute()) {
                    return;
                }
                if (Date.now() - this.lastActivity() < this.inactivityTimeout) {
                    return;
                }

                this.isLoggingOut = true;
                this.ngZone.run(() => {
                    this.stopMonitoring();
                    this.authService.logout().catch((error) => console.error('Error cerrando sesión por inactividad:', error));
                });
            });
        });
    }

    // Stop the inactivity timer
    private stopInactivityTimer(): void {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }
    }
}
